import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Snackbar, Stack, TablePagination, Typography } from '@mui/material';
import {
  exportSupportTickets,
  fetchCustomers,
  fetchSupportAssignees,
  fetchSupportTickets,
  fetchTenants,
} from '../api/authApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { CreateSupportTicketDialog } from '../components/CreateSupportTicketDialog';
import { DataTable } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';
import { getSupportPriorityLabel, getSupportStatusLabel } from '../utils/statusLabels';
import {
  supportTicketDisplayId,
  supportTicketIsUnread,
  supportTicketLastActivity,
  supportTicketSubject,
} from '../utils/supportTicketIdentity';

const TICKET_STATUSES = [
  'open',
  'waiting_for_support',
  'waiting_for_customer',
  'resolved',
  'closed',
];

const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const PAGE_SIZE_OPTIONS = [10, 25, 50];

function formatDate(value, emptyValue) {
  if (!value) {
    return emptyValue;
  }

  return new Date(value).toLocaleString();
}

export function SupportInboxPage() {
  const { t } = useTranslation(['support', 'common', 'statuses']);
  const navigate = useNavigate();
  const { session } = useAuth();
  const canCreate = hasPermission(session, 'system.support.reply');
  const canView = hasPermission(session, 'system.support.view');
  const emptyValue = t('common:empty_value');

  const [tickets, setTickets] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 25, total: 0 });
  const [customers, setCustomers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [unreadOnly, setUnreadOnly] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSuccess, setCreateSuccess] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const filtersActive = Boolean(
    search
    || status
    || priority
    || customerId
    || tenantId
    || assigneeFilter
    || unreadOnly
  );

  const tenantOptions = useMemo(() => {
    if (!customerId) {
      return tenants;
    }
    return tenants.filter((tenant) => String(tenant.customer_id) === String(customerId));
  }, [customerId, tenants]);

  const resetPage = () => setPage(1);

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatus('');
    setPriority('');
    setCustomerId('');
    setTenantId('');
    setAssigneeFilter('');
    setUnreadOnly('');
    setPage(1);
  };

  const handleCustomerChange = (nextCustomerId) => {
    setCustomerId(nextCustomerId);
    setTenantId('');
    resetPage();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchCustomers({ per_page: 100 })
      .then((response) => setCustomers(response.data || []))
      .catch(() => setCustomers([]));
    fetchTenants({ per_page: 100 })
      .then((response) => setTenants(response.data || []))
      .catch(() => setTenants([]));
    fetchSupportAssignees()
      .then(setAssignees)
      .catch(() => setAssignees([]));
  }, []);

  const loadTickets = useCallback(() => {
    setLoading(true);
    setError(null);
    void reloadKey;

    const params = {
      page,
      per_page: perPage,
    };
    if (search) params.search = search;
    if (status) params.status = status;
    if (priority) params.priority = priority;
    if (customerId) params.customer_id = customerId;
    if (tenantId) params.tenant_id = tenantId;
    if (assigneeFilter === 'unassigned') {
      params.unassigned = true;
    } else if (assigneeFilter) {
      params.assigned_to_user_id = assigneeFilter;
    }
    if (unreadOnly === 'true') params.unread = true;

    fetchSupportTickets(params)
      .then((response) => {
        setTickets(response.data || []);
        const nextMeta = response.meta || {};
        setMeta({
          current_page: nextMeta.current_page || page,
          last_page: nextMeta.last_page || 1,
          per_page: nextMeta.per_page || perPage,
          total: nextMeta.total || 0,
        });
      })
      .catch(() => {
        setTickets([]);
        setError(t('support:load_tickets_failed'));
      })
      .finally(() => setLoading(false));
  }, [
    page,
    perPage,
    search,
    status,
    priority,
    customerId,
    tenantId,
    assigneeFilter,
    unreadOnly,
    reloadKey,
    t,
  ]);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    loadTickets();
  }, [canView, loadTickets]);

  const handleExport = async () => {
    try {
      const blob = await exportSupportTickets({
        search: search || undefined,
        status: status || undefined,
        priority: priority || undefined,
        customer_id: customerId || undefined,
        tenant_id: tenantId || undefined,
        assigned_to_user_id: assigneeFilter && assigneeFilter !== 'unassigned' ? assigneeFilter : undefined,
        unassigned: assigneeFilter === 'unassigned' || undefined,
        unread: unreadOnly === 'true' || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `support-tickets-${Date.now()}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t('support:export_failed'));
    }
  };

  const columns = [
    {
      id: 'ticket',
      label: t('support:column_ticket'),
      render: (ticket) => (
        <Stack direction="row" spacing={1} alignItems="center">
          {supportTicketIsUnread(ticket) ? (
            <Box
              aria-label={t('support:unread')}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                flexShrink: 0,
              }}
            />
          ) : (
            <Box sx={{ width: 8, flexShrink: 0 }} />
          )}
          <Typography
            variant="body2"
            sx={{ fontWeight: supportTicketIsUnread(ticket) ? 600 : 500, fontFamily: 'ui-monospace, monospace' }}
          >
            {supportTicketDisplayId(ticket) || emptyValue}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'subject',
      label: t('support:column_subject'),
      render: (ticket) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: supportTicketIsUnread(ticket) ? 600 : 400 }}
        >
          {supportTicketSubject(ticket) || emptyValue}
        </Typography>
      ),
    },
    {
      id: 'customer_tenant',
      label: t('support:column_customer_tenant'),
      render: (ticket) => (
        <Box>
          <Typography variant="body2">{ticket.customer?.name || emptyValue}</Typography>
          <Typography variant="caption" color="text.secondary">
            {ticket.tenant?.name || ticket.tenant?.slug || emptyValue}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'requester',
      label: t('support:column_requester'),
      render: (ticket) => ticket.requested_by?.name
        || ticket.requested_by_name
        || emptyValue,
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (ticket) => <StatusBadge status={ticket.status} category="support" size="Sm" />,
    },
    {
      id: 'priority',
      label: t('support:tickets.priority'),
      render: (ticket) => <StatusBadge status={ticket.priority} category="priority" size="Sm" />,
    },
    {
      id: 'assignee',
      label: t('support:assigned_to'),
      render: (ticket) => ticket.assigned_to?.name || t('support:unassigned'),
    },
    {
      id: 'last_activity',
      label: t('support:column_last_activity'),
      render: (ticket) => formatDate(supportTicketLastActivity(ticket), emptyValue),
    },
  ];

  if (!canView) {
    return (
      <Box>
        <PageHeader type="List" title={t('support:inbox')} subtitle={t('support:inbox_subtitle')} />
        <ErrorState message={t('support:load_tickets_failed')} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        type="List"
        title={t('support:inbox')}
        subtitle={t('support:inbox_subtitle')}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="Secondary" onClick={handleExport}>
              {t('support:export_csv')}
            </Button>
            {canCreate ? (
              <Button variant="Primary" onClick={() => setCreateOpen(true)}>
                {t('support:new_ticket')}
              </Button>
            ) : null}
          </Stack>
        )}
      />

      <FilterBar type="Simple">
        <TextInput
          density="Compact"
          hideLabel
          label={t('common:search')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('support:search_placeholder')}
          inputProps={{ 'data-testid': 'support-inbox-search' }}
        />
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            resetPage();
          }}
          minWidth={150}
          inputProps={{ 'data-testid': 'support-inbox-status' }}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {TICKET_STATUSES.map((item) => (
            <FilterSelectOption key={item} value={item}>
              {getSupportStatusLabel(item)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('support:tickets.priority')}
          hideLabel
          value={priority}
          onChange={(e) => {
            setPriority(e.target.value);
            resetPage();
          }}
          minWidth={130}
          inputProps={{ 'data-testid': 'support-inbox-priority' }}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {PRIORITIES.map((item) => (
            <FilterSelectOption key={item} value={item}>
              {getSupportPriorityLabel(item)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('common:customer')}
          hideLabel
          value={customerId}
          onChange={(e) => handleCustomerChange(e.target.value)}
          minWidth={150}
          inputProps={{ 'data-testid': 'support-inbox-customer' }}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {customers.map((customer) => (
            <FilterSelectOption key={customer.id} value={String(customer.id)}>
              {customer.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('common:tenant')}
          hideLabel
          value={tenantId}
          onChange={(e) => {
            setTenantId(e.target.value);
            resetPage();
          }}
          minWidth={150}
          inputProps={{ 'data-testid': 'support-inbox-tenant' }}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {tenantOptions.map((tenant) => (
            <FilterSelectOption key={tenant.id} value={String(tenant.id)}>
              {tenant.name || tenant.slug}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('support:assigned_to')}
          hideLabel
          value={assigneeFilter}
          onChange={(e) => {
            setAssigneeFilter(e.target.value);
            resetPage();
          }}
          minWidth={160}
          inputProps={{ 'data-testid': 'support-inbox-assignee' }}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          <FilterSelectOption value="unassigned">{t('support:unassigned')}</FilterSelectOption>
          {assignees.map((user) => (
            <FilterSelectOption key={user.id} value={String(user.id)}>
              {user.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('support:unread')}
          hideLabel
          value={unreadOnly}
          onChange={(e) => {
            setUnreadOnly(e.target.value);
            resetPage();
          }}
          minWidth={130}
          inputProps={{ 'data-testid': 'support-inbox-unread' }}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          <FilterSelectOption value="true">{t('support:unread_only')}</FilterSelectOption>
        </FilterSelect>
        {filtersActive ? (
          <Button variant="Ghost" onClick={clearFilters}>
            {t('support:clear_filters')}
          </Button>
        ) : null}
      </FilterBar>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ErrorState message={error} onRetry={loadTickets} />
        </Alert>
      ) : null}

      {!error && !loading && tickets.length === 0 ? (
        <EmptyState
          message={filtersActive ? t('support:no_tickets_match_filters') : t('support:no_tickets_yet')}
          action={(
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
              {filtersActive ? (
                <Button variant="Secondary" onClick={clearFilters}>
                  {t('support:clear_filters')}
                </Button>
              ) : null}
              {!filtersActive && canCreate ? (
                <Button variant="Primary" onClick={() => setCreateOpen(true)}>
                  {t('support:new_ticket')}
                </Button>
              ) : null}
            </Stack>
          )}
        />
      ) : (
        <SectionCard variant="Default" noPadding>
          <TableToolbar
            mode="Default"
            title={t('support:inbox')}
            meta={(
              <StatusBadge
                status="list"
                category="generic"
                apiLabel={t('support:toolbar_count', { count: meta.total || tickets.length })}
                size="Sm"
                tone="Neutral"
              />
            )}
          />
          {loading ? (
            <Box role="status" aria-busy="true" data-testid="support-inbox-loading">
              <Typography component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                Loading
              </Typography>
              <LoadingSkeleton type="Table" embedded rows={3} columns={columns.length} />
            </Box>
          ) : (
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <DataTable
                type="Default"
                embedded
                columns={columns}
                rows={tickets}
                emptyMessage={t('support:no_tickets_found')}
                onRowClick={(ticket) => navigate(`/support/${ticket.id}`)}
              />
            </Box>
          )}
        </SectionCard>
      )}

      {!error && (meta.total > 0 || loading) ? (
        <TablePagination
          component="div"
          count={meta.total}
          page={Math.max(0, (meta.current_page || page) - 1)}
          onPageChange={(_event, nextPage) => setPage(nextPage + 1)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(event) => {
            setPerPage(Number(event.target.value));
            setPage(1);
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          labelRowsPerPage={t('support:rows_per_page')}
        />
      ) : null}

      <CreateSupportTicketDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        customers={customers}
        tenants={tenants}
        onCreated={(ticket) => {
          setReloadKey((k) => k + 1);
          if (ticket?.public_number) {
            setCreateSuccess(t('support:ticket_created_number', { number: ticket.public_number }));
          }
          navigate(`/support/${ticket.id}`);
        }}
      />

      <Snackbar
        open={Boolean(createSuccess)}
        autoHideDuration={5000}
        onClose={() => setCreateSuccess('')}
        message={createSuccess}
      />
    </Box>
  );
}
